// __ROUTE_DRAFTS__ (2026-10-03) Speculative routes for the Route Builder.
//
// Henry: "Route building must be speculative at first so I can sneak jobs from
// various drivers as per what's assigned, then test out various routes, and once
// decided I can finalize it; then I can set who that entire route is assigned to
// or even complete it as unassigned for the time being."
//
// A draft (public.route_drafts + route_draft_loads) changes NOTHING real. Jobs
// stay on their real driver / route until finalizeDraft():
//   1. re-reads every job LIVE and sorts it with planFinalize (finished jobs are
//      never moved; jobs on another driver or already started need one confirm);
//   2. writes loads.driver_id / scheduled_date / status (the driver app's
//      "Job taken off you" fires on its next sync for anyone who lost a job);
//   3. takes the jobs off every other route (route_loads + driver_daily_routes)
//      and re-plans each driver who lost one (or clears his day);
//   4. builds the target driver's day in the draft's order (stopOrder, no Google
//      re-ordering), mirrors it to routes/route_loads and stamps the stop plan
//      (plan_source build_routes, so the hub planner keeps it) - or, for
//      Unassigned, keeps the group as an open route (routes.driver_id null);
//   5. marks the draft finalized.
import { supabase } from '../supabaseClient';
import { logLoadActivity } from './activityLogService';   // __BOARD_ACTIVITY__
import {
  calculateDriverDailyRoute,
  persistDriverAppRoute,
  detachLoadFromDriverRoutes,
  clearLoadStopETAs,
  recomputeOrClearDriverRoute,
  geocodeAddress,
  getRoutingConfig,
} from './routingEngineService';
import { resolveStopDwell, loadStopTimeSettings } from './dwellService';
import { parseWindowTime } from '../utils/dbTime';   // __TZ_WINDOWS__ windows are Toronto wall clock
import {
  planFinalize, finalizeLoadPatch, routeNumberFor, draftStopOrder, orderedStops,
  precedencePairs, optimizeStopOrder, estimateDriveSeconds, loadOrderFromStopOrder,
} from '../utils/routeBuilderModel';

const nowIso = () => new Date().toISOString();

// ---------------------------------------------------------------- reads
export const listOpenDrafts = async () => {
  const { data, error } = await supabase
    .from('route_drafts')
    .select('id, name, route_date, status, notes, stop_order, created_at, updated_at, route_draft_loads(load_id, seq)')
    .eq('status', 'draft')
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map((d) => ({
    ...d,
    loadIds: [...(d.route_draft_loads || [])].sort((a, b) => a.seq - b.seq).map((r) => r.load_id),
  }));
};

// Real routes for a day (driver routes + open/unassigned ones), with their jobs in order.
export const fetchRoutesForDate = async (date) => {
  const { data, error } = await supabase
    .from('routes')
    .select('id, route_number, driver_id, status, planned_date, total_stops, route_loads(load_id, sequence_order)')
    .eq('planned_date', date)
    .in('status', ['planned', 'active'])
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map((r) => ({
    ...r,
    loadIds: [...(r.route_loads || [])].sort((a, b) => a.sequence_order - b.sequence_order).map((x) => x.load_id),
  }));
};

export const fetchFullLoads = async (loadIds) => {
  if (!loadIds || !loadIds.length) return [];
  const { data, error } = await supabase.from('loads').select('*, load_stops (*)').in('id', loadIds);
  if (error) throw error;
  const byId = new Map((data || []).map((l) => [l.id, l]));
  return loadIds.map((id) => byId.get(id)).filter(Boolean);
};

// ---------------------------------------------------------------- draft writes
export const createDraft = async ({ name, routeDate, loadIds = [], stopOrder = null }) => {
  const { data, error } = await supabase
    .from('route_drafts')
    .insert({ name: name || 'Draft route', route_date: routeDate || null, stop_order: stopOrder })
    .select('id, name, route_date, status, notes, stop_order, created_at, updated_at')
    .single();
  if (error) throw error;
  if (loadIds.length) await setDraftLoads(data.id, loadIds, stopOrder);
  return { ...data, loadIds: loadIds.slice() };
};

// Replace a draft's job list (ordered). stopOrder: keep an optimiser order, or
// null when the office re-arranged by hand (jobs in order, pickups first).
export const setDraftLoads = async (draftId, loadIds, stopOrder = null) => {
  const { error: delErr } = await supabase.from('route_draft_loads').delete().eq('draft_id', draftId);
  if (delErr) throw delErr;
  if (loadIds.length) {
    const rows = loadIds.map((id, i) => ({ draft_id: draftId, load_id: id, seq: i + 1 }));
    const { error } = await supabase.from('route_draft_loads').insert(rows);
    if (error) throw error;
  }
  const { error: upErr } = await supabase.from('route_drafts')
    .update({ stop_order: stopOrder, updated_at: nowIso() }).eq('id', draftId);
  if (upErr) throw upErr;
};

export const updateDraftMeta = async (draftId, patch) => {
  const allowed = {};
  ['name', 'route_date', 'notes'].forEach((k) => { if (k in patch) allowed[k] = patch[k]; });
  allowed.updated_at = nowIso();
  const { error } = await supabase.from('route_drafts').update(allowed).eq('id', draftId);
  if (error) throw error;
};

export const discardDraft = async (draftId) => {
  const { error } = await supabase.from('route_drafts')
    .update({ status: 'discarded', updated_at: nowIso() }).eq('id', draftId);
  if (error) throw error;
};

// ---------------------------------------------------------------- suggest order
// Drive times between every pair of points: Google Distance Matrix with the
// departure time (traffic) when the Maps JS API is loaded, else the engine's
// straight-line estimate. points: [{lat,lng}]. Returns seconds[i][j] + source.
export const driveMatrix = async (points, departAt) => {
  const n = points.length;
  const est = () => points.map((a) => points.map((b) => (a === b ? 0 : estimateDriveSeconds(a, b))));
  const g = typeof window !== 'undefined' && window.google && window.google.maps;
  if (!g || !g.DistanceMatrixService || n < 2 || n > 25) return { dur: est(), source: 'estimate' };
  try {
    const svc = new g.DistanceMatrixService();
    const dur = Array.from({ length: n }, () => new Array(n).fill(0));
    const ll = points.map((p) => new g.LatLng(p.lat, p.lng));
    const rowsPer = Math.max(1, Math.floor(100 / n));   // 100 elements per request
    for (let r0 = 0; r0 < n; r0 += rowsPer) {
      const origins = ll.slice(r0, r0 + rowsPer);
      const req = { origins, destinations: ll, travelMode: g.TravelMode.DRIVING, unitSystem: g.UnitSystem.METRIC };
      if (departAt) req.drivingOptions = { departureTime: departAt, trafficModel: g.TrafficModel.BEST_GUESS };
      // eslint-disable-next-line no-await-in-loop
      const res = await new Promise((resolve, reject) => svc.getDistanceMatrix(req, (resp, status) => (status === 'OK' ? resolve(resp) : reject(new Error(status)))));
      res.rows.forEach((row, ri) => row.elements.forEach((el, j) => {
        const i = r0 + ri;
        if (i === j) return;
        const v = el && el.status === 'OK' ? ((el.duration_in_traffic && el.duration_in_traffic.value) || el.duration.value) : null;
        dur[i][j] = v != null ? v : estimateDriveSeconds(points[i], points[j]);
      }));
    }
    return { dur, source: departAt ? 'google_traffic' : 'google' };
  } catch (e) {
    console.warn('distance matrix failed, using estimates:', e);
    return { dur: est(), source: 'estimate' };
  }
};

const cargoFields = (load) => ({
  loadId: load.id, loadNumber: load.load_number, commodity: load.commodity,
  cargo_type: load.commodity || null,
  // the loads columns' names lie: *_cm hold FEET, weight_kg POUNDS (see dwellService)
  weight_lb: load.weight_kg, length_ft: load.length_cm, width_ft: load.width_cm, height_ft: load.height_cm,
});

// Best order for a set of jobs: each job's pickups before its drop-offs, using
// drive times (traffic) + each stop's time from the one dwell resolver (cargo
// type, size/weight tier, learned place...) + time windows. Then the real
// engine times it (dry run) so the draft shows the same timeline a built route
// would. Returns { stopOrder, loadOrder, timeline, failedStops, matrixSource }.
export const suggestOrder = async (loads, date) => {
  const config = getRoutingConfig();
  const stops = [];
  const failedStops = [];
  const stopToLoad = {};
  for (const load of loads) {
    for (const st of orderedStops(load)) {
      let lat = st.latitude != null ? parseFloat(st.latitude) : null;
      let lng = st.longitude != null ? parseFloat(st.longitude) : null;
      if ((lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) && st.address) {
        // eslint-disable-next-line no-await-in-loop
        const g = await geocodeAddress(st.address);
        if (g && g.confidence >= 0.99) { lat = g.lat; lng = g.lng; }   // Google only - not the simulated city guess
      }
      stopToLoad[st.id] = load.id;
      if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) {
        failedStops.push({ loadNumber: load.load_number, stopType: st.stop_type, address: st.address || '(no address)' });
        continue;
      }
      // eslint-disable-next-line no-await-in-loop
      const dwell = await resolveStopDwell({ ...st, ...cargoFields(load) });
      stops.push({ id: st.id, loadId: load.id, stop_type: st.stop_type, lat, lng, dwellMin: (dwell && dwell.minutes) || 30,
        windowStart: st.time_window_start ? parseWindowTime(st.time_window_start) : null,
        windowEnd: st.time_window_end ? parseWindowTime(st.time_window_end) : null });
    }
  }
  const yard = (config.startFromYard !== false && config.homeLat != null && config.homeLng != null)
    ? { lat: Number(config.homeLat), lng: Number(config.homeLng) } : null;
  // start time: the day start on the route date (or now, if that is today and later)
  const [dy, dm, dd] = String(date).split('-').map((x) => parseInt(x, 10));
  const [sh, sm] = String(config.dayStartTime || '07:00').split(':').map((x) => parseInt(x, 10));
  let start = new Date(dy, (dm || 1) - 1, dd || 1, Number.isNaN(sh) ? 7 : sh, Number.isNaN(sm) ? 0 : sm, 0, 0);
  if (start.getTime() < Date.now()) {
    const t = new Date();
    if (t.getFullYear() === dy && t.getMonth() + 1 === dm && t.getDate() === dd) start = t;
  }
  let prep = 10;
  try { const st = await loadStopTimeSettings(); if (st && Number.isFinite(st.startPrepMin)) prep = st.startPrepMin; } catch (e) { /* default */ }
  start = new Date(start.getTime() + prep * 60000);
  const departAt = start.getTime() >= Date.now() - 15 * 60000 ? new Date(Math.max(start.getTime(), Date.now() + 60000)) : null;

  const points = yard ? [yard, ...stops] : stops;
  const { dur: full, source } = await driveMatrix(points, departAt);
  const off = yard ? 1 : 0;
  const n = stops.length;
  const m = {
    n,
    dur: Array.from({ length: n }, (_, i) => Array.from({ length: n }, (__, j) => full[i + off][j + off])),
    fromStart: Array.from({ length: n }, (_, j) => (yard ? full[0][j + 1] : 0)),
    dwell: stops.map((s) => s.dwellMin),
    windows: stops.map((s) => ({ start: s.windowStart ? s.windowStart.getTime() : null, end: s.windowEnd ? s.windowEnd.getTime() : null })),
    startTime: start.getTime(),
    pairs: precedencePairs(stops),
  };
  const best = optimizeStopOrder(m);
  const routedIds = (best.order || []).map((i) => stops[i].id);
  // stops that could not be placed (no position) keep their job's order at the end
  const unplaced = [];
  loads.forEach((l) => orderedStops(l).forEach((s) => { if (!routedIds.includes(s.id)) unplaced.push(s.id); }));
  const stopOrder = [...routedIds, ...unplaced];
  const loadOrder = loadOrderFromStopOrder(stopOrder, stopToLoad);
  let timeline = null;
  try {
    timeline = await calculateDriverDailyRoute(null, date, { dryRun: true, loadsOverride: loads, stopOrder });
  } catch (e) { console.warn('draft timeline failed:', e); }
  return { stopOrder, loadOrder, timeline, failedStops, matrixSource: source, exact: best.exact };
};

// Timeline (dry run) for a draft in its current order - nothing written.
export const draftTimeline = async (loadsInOrder, date, savedStopOrder) => {
  const stopOrder = draftStopOrder(loadsInOrder, savedStopOrder);
  return calculateDriverDailyRoute(null, date, { dryRun: true, loadsOverride: loadsInOrder, stopOrder });
};

// ---------------------------------------------------------------- finalize
export const readLiveLoads = async (loadIds) => {
  const { data, error } = await supabase
    .from('loads')
    .select('id, load_number, status, started_at, driver_id, scheduled_date')
    .in('id', loadIds);
  if (error) throw error;
  const byId = new Map((data || []).map((l) => [l.id, l]));
  return loadIds.map((id) => byId.get(id)).filter(Boolean);
};

// Step 1 for the UI: what finalizing would do (show it, ask the confirm).
export const previewFinalize = async ({ loadIds, targetDriverId, date }) => {
  const live = await readLiveLoads(loadIds);
  return { live, plan: planFinalize(live, targetDriverId || null, date) };
};

// Steps 2-5. confirmed must be true when plan.needsConfirm. Never throws for a
// single job's failure - returns { ok, moved, skipped, errors, routeId }.
export const finalizeDraft = async ({ draft, loadIds, stopOrder, targetDriverId, date, confirmed = false }) => {
  const out = { ok: false, moved: [], skipped: [], errors: [], routeId: null };
  const target = targetDriverId || null;
  const day = String(date || draft.route_date || '').slice(0, 10);
  if (!day) { out.errors.push('Pick a day for the route first.'); return out; }
  const { live, plan } = await previewFinalize({ loadIds, targetDriverId: target, date: day });
  if (plan.needsConfirm && !confirmed) { out.errors.push('needs_confirm'); return out; }
  plan.blocked.forEach((l) => out.skipped.push({ load: l, reason: 'finished (' + l.status + ')' }));
  const blocked = new Set(plan.blocked.map((l) => l.id));
  const movers = live.filter((l) => !blocked.has(l.id));
  if (!movers.length) { out.errors.push('Nothing to finalize - every job in this draft is finished.'); return out; }
  const moverIds = movers.map((l) => l.id);

  // where the jobs are now (every route they sit on), before anything changes
  const { data: oldRl } = await supabase
    .from('route_loads').select('route_id, load_id, routes(id, driver_id, planned_date)').in('load_id', moverIds);
  const touched = new Map();   // key driver|date -> {driverId, date}
  const openRoutes = new Set();
  (oldRl || []).forEach((r) => {
    const rt = r.routes || {};
    if (rt.driver_id) { if (rt.driver_id !== target) touched.set(rt.driver_id + '|' + rt.planned_date, { driverId: rt.driver_id, date: rt.planned_date }); }
    else if (rt.id) openRoutes.add(rt.id);
  });
  movers.forEach((l) => {
    if (l.driver_id && l.driver_id !== target) {
      const d = String(l.scheduled_date || day).slice(0, 10);
      touched.set(l.driver_id + '|' + d, { driverId: l.driver_id, date: d });
    }
  });

  // 2. the loads rows
  for (const l of movers) {
    const patch = finalizeLoadPatch(l, target, day);
    // eslint-disable-next-line no-await-in-loop
    const { error } = await supabase.from('loads').update(patch).eq('id', l.id);
    if (error) { out.errors.push((l.load_number || l.id) + ': ' + error.message); continue; }
    out.moved.push(l);
    logLoadActivity(l.id, 'route_planned', 'driver_id', l.driver_id || null, target, 'for ' + day);   // __BOARD_ACTIVITY__
  }
  const movedIds = out.moved.map((l) => l.id);
  if (!movedIds.length) return out;

  // 3. off every other route + re-plan the drivers who lost a job
  try { await supabase.from('route_loads').delete().in('load_id', movedIds); } catch (e) { console.warn('route_loads clean-up failed:', e); }
  for (const l of out.moved) {
    if (l.driver_id && l.driver_id !== target) {
      // eslint-disable-next-line no-await-in-loop
      await detachLoadFromDriverRoutes(l.id, l.driver_id);
    }
  }
  for (const t of touched.values()) {
    // eslint-disable-next-line no-await-in-loop
    await recomputeOrClearDriverRoute(t.driverId, t.date, 'reassign');
  }
  for (const rid of openRoutes) {
    // eslint-disable-next-line no-await-in-loop
    const { data: left } = await supabase.from('route_loads').select('load_id').eq('route_id', rid);
    if (!left || !left.length) await supabase.from('routes').delete().eq('id', rid);
    else await supabase.from('routes').update({ total_stops: left.length, updated_at: nowIso() }).eq('id', rid);
  }

  // 4. the target
  const order = (stopOrder && stopOrder.length) ? stopOrder : null;
  if (target) {
    let planned = false;
    try {
      const result = await calculateDriverDailyRoute(target, day, order ? { stopOrder: order } : {});
      if (result && result.success) {
        await persistDriverAppRoute(target, result, 'build_routes');
        planned = true;
      }
    } catch (e) { console.warn('target route plan failed:', e); }
    if (!planned) {
      // no routable stops (addresses not found): still give him the route rows
      await writeRouteRows({ driverId: target, date: day, loadIds: movedIds, draftId: draft.id });
    }
    const { data: rt } = await supabase.from('routes').select('id').eq('driver_id', target).eq('planned_date', day).limit(1);
    out.routeId = rt && rt[0] ? rt[0].id : null;
  } else {
    for (const id of movedIds) {
      // eslint-disable-next-line no-await-in-loop
      await clearLoadStopETAs(id);
    }
    out.routeId = await writeRouteRows({ driverId: null, date: day, loadIds: movedIds, draftId: draft.id });
  }

  // 5. the draft
  const { error: dErr } = await supabase.from('route_drafts').update({
    status: 'finalized', route_date: day, finalized_driver_id: target, finalized_route_id: out.routeId,
    finalized_at: nowIso(), updated_at: nowIso(),
  }).eq('id', draft.id);
  if (dErr) out.errors.push('Draft not marked finalized: ' + dErr.message);
  try {
    const { logActivity } = await import('./activityLogService');
    await logActivity('route_finalized', 'route', out.routeId, null,
      { draft: draft.name, driver_id: target, date: day, loads: out.moved.map((l) => l.load_number || l.id) },
      'Route "' + (draft.name || 'Draft') + '" finalized: ' + out.moved.length + ' job(s) ' + (target ? 'to a driver' : 'left unassigned') + ' for ' + day);
  } catch (e) { /* logging never blocks */ }
  out.ok = out.errors.length === 0;
  return out;
};

// A routes row + its route_loads in this order, without a computed plan (the
// open / unassigned route, or a driver day whose stops could not be routed).
export const writeRouteRows = async ({ driverId, date, loadIds, draftId }) => {
  const payload = {
    driver_id: driverId || null, status: 'planned', planned_date: date,
    total_stops: loadIds.length, updated_at: nowIso(),
  };
  let routeId = null;
  if (driverId) {
    const { data: ex } = await supabase.from('routes').select('id').eq('driver_id', driverId).eq('planned_date', date).limit(1);
    if (ex && ex.length) {
      routeId = ex[0].id;
      await supabase.from('routes').update(payload).eq('id', routeId);
      await supabase.from('route_loads').delete().eq('route_id', routeId);
    }
  }
  if (!routeId) {
    payload.route_number = routeNumberFor(date, driverId, draftId);
    const { data: ins, error } = await supabase.from('routes').insert(payload).select('id').single();
    if (error || !ins) { console.warn('route insert failed:', error); return null; }
    routeId = ins.id;
  }
  const rows = loadIds.map((id, i) => ({ route_id: routeId, load_id: id, sequence_order: i + 1 }));
  const { error: rlErr } = await supabase.from('route_loads').insert(rows);
  if (rlErr) console.warn('route_loads insert failed:', rlErr);
  return routeId;
};

const routeDraftService = {
  listOpenDrafts, fetchRoutesForDate, fetchFullLoads, createDraft, setDraftLoads, updateDraftMeta,
  discardDraft, suggestOrder, draftTimeline, previewFinalize, finalizeDraft, writeRouteRows, driveMatrix,
};
export default routeDraftService;
